import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BulkCashReceiptCompletionStatus } from 'app/models/miscellaneous-billing/BulkCashReceiptFormView';
import { CashReceiptFormView } from 'app/models/miscellaneous-billing/CashReceiptFormView';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { UtilityService } from '../utility.service';

@Injectable({
	providedIn: 'root',
})
export class BulkCashReceiptFormService {
	private readonly urlPath = `${environment.apiUrl}miscellaneous-billing/clients/`;
	headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0' });
	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	createForm(clientId: string, request: any): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/bulk-cash-receipt-form`, request).pipe();
	}

	updateForm(clientId: string, id: string, request: any): Observable<any> {
		return this.httpClient.put<any>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}`, request).pipe();
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}`).pipe();
	}

	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	// Durable status of a completed form's background posting; polled by the form page.
	getCompletionStatus(clientId: string, id: string): Observable<BulkCashReceiptCompletionStatus> {
		return this.httpClient
			.get<BulkCashReceiptCompletionStatus>(
				`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}/completion-status`,
				{ headers: this.headers }
			)
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${
			this.urlPath
		}${clientId}/bulk-cash-receipt-form/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<CashReceiptFormView> {
		return this.httpClient
			.post<CashReceiptFormView>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${formId}/docs/${docId}`);
	}

	receipt(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}/receipt`).pipe();
	}
	getRecurringForms(clientId: string): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/recurring-forms`, { headers: this.headers })
			.pipe();
	}

	getCashieringAdjustments(clientId: string, id: string): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/bulk-cash-receipt-form/${id}/cashiering-adjustments`)
			.pipe();
	}
}
